import React, { FC, forwardRef, useCallback, useState } from 'react';
import styled from 'styled-components';

import { getCampaignDaysLeft } from '../../shared/product/ProductCampaignEndDate';
import MetadataUtils from '../../shared/utils/utils-metadata';
import { CAMPAIGN_PRODUCTS, useFeature } from '../../shared/utils/togglz';
import { THEME_WARNING, themeColor } from '../../styles/basics/colors';
import { PRODUCT_CLASS_CAMPAIGN } from '../../store/constants';
import { Article } from '../../../models/Article';
import { ProductMetaData } from '../../../models/Product';
import { useDebouncedCallback } from '../../shared/hooks/useDebouncedCallback';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { useTranslation } from 'react-i18next';
import FormatCurrency from '../../shared/utils/formatCurrency';
import { SingleAddOptions } from '../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../models/ListActionTarget';

const StSubDiscountLine = styled.div`
    margin-top: 2px;
    font-weight: bold;
    color: ${themeColor(THEME_WARNING)};
    margin-bottom: 5px;
`;

type Props = {
    readonly product: ProductMetaData & Article;
    readonly onAddPrice: (options: SingleAddOptions) => Promise<void>;
};

const CartPriceActionButton = forwardRef<HTMLDivElement, Props>(({ product, onAddPrice }, ref) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    const [handleClick] = useDebouncedCallback(
        useCallback(() => {
            setLoading(true);
            const addOptions: SingleAddOptions = { options: product, target: ListActionTarget.Cart };
            onAddPrice(addOptions).finally(() => setLoading(false));
        }, [product, onAddPrice]),
        1600,
        {
            leading: true,
            trailing: false,
            maxWait: 1600
        }
    );

    const isUnavailable = !MetadataUtils.isProductAvailableForSale(product.price);
    const isComingSoon = product.salesStatus && product.salesStatus.key === 'COMING_SOON';
    const isDiscounted = !!product.price?.basePrice;

    if (isComingSoon) {
        return (
            <div ref={ref} className="bt-line">
                <div className="strong">Coming Soon</div>
            </div>
        );
    }

    if (isUnavailable) {
        return (
            <div ref={ref} className="bt-line bt-price">
                <div className="cold-grey strong">Product unavailable</div>
            </div>
        );
    }

    return (
        <div ref={ref} className="bt-line bt-price">
            <Button
                data-testid="cart-price-button"
                text={t('addToCart')}
                textSecondary={t('numberFormat.numberWithCurrencySymbol', {
                    val: FormatCurrency.convertIntegerToCurrencyFormat(product.price?.netValue),
                    currencySymbol: FormatCurrency.mapCurrencyToSymbol(product.price?.currency)
                })}
                highlightSecondaryText={isDiscounted}
                icon={'shopping_cart'}
                size={ButtonSize.SMALL}
                theme={MetadataUtils.isProductPreOrderable(product) ? ButtonTheme.WARNING_INVERT : ButtonTheme.NEUTRAL}
                onClick={handleClick}
                fullWidth={true}
                disabled={loading}
                isLoading={loading}
            />
            <DiscountLine product={product} />
            <SubDiscountLine product={product} />
        </div>
    );
});

CartPriceActionButton.displayName = 'CartPriceActionButton';

export default CartPriceActionButton;

const DiscountLine: FC<{
    readonly product: ProductMetaData & Article;
}> = ({ product }) => {
    const { t } = useTranslation();
    const isDiscounted = !!product.price?.basePrice;

    if (!isDiscounted) {
        return null;
    }

    return (
        <span className="discount">
            {t('reserveReports.reserveReport.viewReport.save', {
                amount: t('numberFormat.numberWithCurrencySymbol', {
                    val: FormatCurrency.convertIntegerToCurrencyFormat(product.price?.savedValue),
                    currencySymbol: FormatCurrency.mapCurrencyToSymbol(product.price?.currency)
                }),
                percentile: Math.round((product.price?.savedPercentage ?? 0) / 100)
            })}
            <span className="fl-right crossed">
                {t('numberFormat.numberWithCurrencySymbol', {
                    val: FormatCurrency.convertIntegerToCurrencyFormat(product.price?.basePrice),
                    currencySymbol: FormatCurrency.mapCurrencyToSymbol(product.price?.currency)
                })}
            </span>
        </span>
    );
};

const SubDiscountLine: FC<{
    readonly product: ProductMetaData & Article;
}> = ({ product }) => {
    const { t } = useTranslation();
    const isCampaignsEnabled = useFeature(CAMPAIGN_PRODUCTS);
    const isCampaign = product.productClass === PRODUCT_CLASS_CAMPAIGN;

    if (MetadataUtils.isProductPreOrderable(product)) {
        return <StSubDiscountLine>Pre-Order</StSubDiscountLine>;
    }

    if (isCampaignsEnabled && isCampaign) {
        const campaignEndDate = product.campaignDetails?.salesEndDate;
        const daysLeft = campaignEndDate ? getCampaignDaysLeft(campaignEndDate, t) : null;
        if (daysLeft !== null && daysLeft[1] <= 10) {
            return <StSubDiscountLine>{daysLeft[0]}</StSubDiscountLine>;
        }
    }

    return null;
};
